/**
 * `vectorStyleModel.ts` — pure helpers for the WP-4 Vector ToolCard.
 *
 * The model surface is intentionally tiny: a `VectorStyleState`
 * snapshot, a `VectorFilter` predicate, and pure deriving functions so
 * the ToolCard, the adapter style hooks, and the unit tests share one
 * source of truth. Keeping the math here (and not in the TSX) means
 * `frontend/src/components/vector/VectorToolCard.tsx` stays focused on
 * presentation.
 */
import { tokens } from '@/design';
import type { Layer } from '@geosoft/viewer-sdk';

// ---------------------------------------------------------------------------
// Token-derived swatches (Vector Card §7.4 "token swatches" requirement)
// ---------------------------------------------------------------------------

export interface VectorSwatch {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** Non-color cue (Material symbol) so the swatch is keyboard / SR friendly. */
  readonly icon:
    | 'verified'
    | 'info'
    | 'warning'
    | 'error'
    | 'public'
    | 'polyline'
    | 'pentagon'
    | 'location_on';
  /** Optional caption / micro-explanation for the swatch. */
  readonly hint?: string;
}

/**
 * Token-driven swatch list. Values resolve to the bridge layer's
 * semantic / accent tokens so component code never hard-codes hex.
 */
export const VECTOR_STROKE_SWATCHES: ReadonlyArray<VectorSwatch> = [
  {
    id: 'accent',
    label: 'Brand accent',
    value: tokens.color.accent.primary,
    icon: 'verified',
    hint: 'Default project-brand stroke.',
  },
  {
    id: 'info',
    label: 'Info',
    value: tokens.color.semantic.info,
    icon: 'info',
    hint: 'Use for advisory / reference geometry.',
  },
  {
    id: 'success',
    label: 'Success',
    value: tokens.color.semantic.success,
    icon: 'verified',
    hint: 'Use for trusted / verified data.',
  },
  {
    id: 'warning',
    label: 'Warning',
    value: tokens.color.semantic.warning,
    icon: 'warning',
    hint: 'Use for stale / requires-review data.',
  },
  {
    id: 'danger',
    label: 'Critical',
    value: tokens.color.semantic.danger,
    icon: 'error',
    hint: 'Use for blockers / conflicts only.',
  },
];

export const VECTOR_FILL_SWATCHES: ReadonlyArray<VectorSwatch> = [
  {
    id: 'accent',
    label: 'Brand accent',
    value: tokens.color.accent.primary,
    icon: 'public',
  },
  {
    id: 'info',
    label: 'Info',
    value: tokens.color.semantic.info,
    icon: 'info',
  },
  {
    id: 'success',
    label: 'Success',
    value: tokens.color.semantic.success,
    icon: 'verified',
  },
  {
    id: 'warning',
    label: 'Warning',
    value: tokens.color.semantic.warning,
    icon: 'warning',
  },
  {
    id: 'subtle',
    label: 'Subtle',
    value: tokens.color.accent.subtle,
    icon: 'public',
  },
];

/**
 * Point-icon swatches: only ligatures already present in
 * `ICON_REGISTRY` are listed so the icon picker can never emit an
 * out-of-registry name (anti-pattern A9).
 */
export const VECTOR_ICON_SWATCHES: ReadonlyArray<{
  readonly id: 'pin' | 'polyline' | 'polygon' | 'flag' | 'verified';
  readonly label: string;
  readonly icon: 'location_on' | 'polyline' | 'pentagon' | 'flag' | 'verified';
}> = [
  { id: 'pin', label: 'Pin', icon: 'location_on' },
  { id: 'polyline', label: 'Line', icon: 'polyline' },
  { id: 'polygon', label: 'Polygon', icon: 'pentagon' },
  { id: 'flag', label: 'Flag', icon: 'flag' },
  { id: 'verified', label: 'Verified', icon: 'verified' },
];

// ---------------------------------------------------------------------------
// Style + filter snapshot
// ---------------------------------------------------------------------------

export interface VectorStyleState {
  readonly strokeSwatchId: string;
  readonly strokeWidth: 1 | 2 | 3 | 4;
  readonly fillSwatchId: string;
  readonly fillOpacity: 0 | 0.1 | 0.2 | 0.3 | 0.5 | 0.7;
  readonly iconSwatchId: 'pin' | 'polyline' | 'polygon' | 'flag' | 'verified';
  readonly labelsEnabled: boolean;
  readonly labelField: string | null;
}

export const DEFAULT_VECTOR_STYLE: VectorStyleState = {
  strokeSwatchId: 'accent',
  strokeWidth: 2,
  fillSwatchId: 'subtle',
  fillOpacity: 0.2,
  iconSwatchId: 'pin',
  labelsEnabled: false,
  labelField: null,
};

export type VectorFilterOperator = 'equals' | 'contains' | 'isEmpty';

export interface VectorFilter {
  readonly attribute: string | null;
  readonly operator: VectorFilterOperator;
  readonly value: string;
}

export const DEFAULT_VECTOR_FILTER: VectorFilter = {
  attribute: null,
  operator: 'equals',
  value: '',
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function resolveSwatch(
  swatches: ReadonlyArray<VectorSwatch>,
  id: string,
): VectorSwatch {
  return (
    swatches.find((swatch) => swatch.id === id) ?? swatches[0] ?? VECTOR_STROKE_SWATCHES[0]
  );
}

export interface MaplibreVectorPaintDelta {
  readonly fillColor?: string;
  readonly fillOpacity?: number;
  readonly lineColor?: string;
  readonly lineWidth?: number;
}

/**
 * Translate a `VectorStyleState` into the paint-property delta that the
 * MapLibre adapter `setVectorPaint` consumes.
 */
export function vectorStyleToPaintDelta(
  state: VectorStyleState,
): MaplibreVectorPaintDelta {
  const stroke = resolveSwatch(VECTOR_STROKE_SWATCHES, state.strokeSwatchId);
  const fill = resolveSwatch(VECTOR_FILL_SWATCHES, state.fillSwatchId);
  return {
    lineColor: stroke.value,
    lineWidth: state.strokeWidth,
    fillColor: fill.value,
    fillOpacity: state.fillOpacity,
  };
}

/**
 * Apply a (simple) `VectorFilter` to a feature property bag. Pure so
 * tests can pin the predicate semantics without React in the loop.
 */
export function featureMatchesFilter(
  properties: Readonly<Record<string, unknown>>,
  filter: VectorFilter,
): boolean {
  if (!filter.attribute) return true;
  const raw = properties[filter.attribute];
  switch (filter.operator) {
    case 'isEmpty':
      return raw === null || raw === undefined || String(raw).trim().length === 0;
    case 'contains':
      return typeof raw === 'string'
        ? raw.toLowerCase().includes(filter.value.trim().toLowerCase())
        : String(raw ?? '').toLowerCase().includes(filter.value.trim().toLowerCase());
    case 'equals':
    default: {
      if (raw === null || raw === undefined) return filter.value.trim().length === 0;
      if (typeof raw === 'number') return Number(filter.value) === raw;
      return String(raw) === filter.value;
    }
  }
}

/**
 * Build a MapLibre filter expression (`['==', ['get', attr], value]`)
 * for the equality / contains / empty cases. Returns `null` when the
 * filter is "all".
 */
export type MaplibreFilterSpec = readonly [string, ...unknown[]] | null;

export function vectorFilterToMaplibre(filter: VectorFilter): MaplibreFilterSpec {
  if (!filter.attribute) return null;
  switch (filter.operator) {
    case 'isEmpty':
      return ['any', ['==', ['get', filter.attribute], ''], ['!', ['has', filter.attribute]]];
    case 'contains':
      return [
        'in',
        filter.value,
        ['to-string', ['get', filter.attribute]],
      ];
    case 'equals':
    default:
      return ['==', ['get', filter.attribute], filter.value];
  }
}

// ---------------------------------------------------------------------------
// OGC / source info
// ---------------------------------------------------------------------------

export interface VectorSourceInfo {
  readonly kind: 'geojson' | 'pmtiles' | 'ogc-features' | 'flatgeobuf' | 'geoparquet' | 'unknown';
  readonly endpoint: string | null;
  readonly featureCount?: number | null;
  readonly supportedCrs?: ReadonlyArray<string>;
  readonly capabilities?: ReadonlyArray<string>;
}

export function deriveVectorSourceInfo(layer: Layer | null): VectorSourceInfo {
  if (!layer) {
    return { kind: 'unknown', endpoint: null };
  }
  const endpoints = layer.endpoints ?? {};
  if (endpoints.features) {
    return {
      kind: 'ogc-features',
      endpoint: endpoints.features,
      supportedCrs: [layer.crs],
      capabilities: ['items', 'collections', 'conformance'],
    };
  }
  if (endpoints.pmtiles) {
    return {
      kind: 'pmtiles',
      endpoint: endpoints.pmtiles,
      supportedCrs: [layer.crs],
      capabilities: ['range-read', 'mvt'],
    };
  }
  const style = layer.style ?? {};
  const declared = typeof style['format'] === 'string' ? style['format'] : null;
  switch (declared) {
    case 'flatgeobuf':
      return { kind: 'flatgeobuf', endpoint: null, supportedCrs: [layer.crs] };
    case 'geoparquet':
      return { kind: 'geoparquet', endpoint: null, supportedCrs: [layer.crs] };
    default:
      return { kind: 'geojson', endpoint: endpoints.tiles ?? null, supportedCrs: [layer.crs] };
  }
}

// ---------------------------------------------------------------------------
// Tool-card-kind gate (mirrors `getToolCardKindFor` for safety)
// ---------------------------------------------------------------------------

const VECTOR_TOOLCARD_LAYER_KINDS: ReadonlySet<string> = new Set([
  'vector',
  'pmtiles',
  'geojson',
  'flatgeobuf',
  'geoparquet',
  'dxf',
  'ogc-features-snapshot',
  'featureLayer',
]);

export function isVectorToolCardLayer(layer: Layer | null | undefined): boolean {
  if (!layer) return false;
  return VECTOR_TOOLCARD_LAYER_KINDS.has(layer.kind);
}
